import { useEffect } from 'react'
import { useFeatureFlag } from './useFeatureFlag'
import useTrackEvent from 'modules/analytics/hooks/useTrackEvent'
import { OptimizelyDecisionWithoutUserContext, OptimizelyFeatureFlags } from 'models/Optimizely'
import { useInternalAnalytics } from 'utils/internalAnalytics'
import { logger } from 'utils/logger'

import mParticleSdk from '@mparticle/web-sdk'

// Global cache to track experiment exposures across the entire app session
const experimentExposures = new Set<string>()

/**
 * Tracks experiment exposure to analytics, with automatic deduplication.
 *
 * Do not call this function if your passed tracking function is not fully initialized
 * (e.g., mParticle)
 *
 * Separated in case there is a critical use case for this outside of our analytics provider
 *
 * This function:
 * 1. Checks if the user is actually assigned to a variation and the experiment is enabled
 * 2. Prevents duplicate tracking for the same experiment variation
 * 3. Calls the provided trackEvent callback to send the analytics event
 *
 * @param decision - Optimizely decision object
 * @param experimentName - Human-readable experiment name for tracking
 * @param trackEvent - Callback function to send the analytics event
 * @returns Whether the exposure was tracked (true) or skipped due to deduplication/disabled (false)
 *
 */
const trackExperimentExposure = (
  decision: OptimizelyDecisionWithoutUserContext,
  experimentName: string,
  trackEvent: typeof mParticleSdk.logEvent,
): boolean => {
  const exposureKey = `${decision.flagKey}:${decision.variationKey}`

  // Bail if decision is disabled
  if (!decision.enabled || !decision.flagKey) {
    logger(
      'error',
      new Error(
        `trackExperimentExposure called with disabled or misconfigured experiment: ${decision.flagKey}.`,
      ),
    )

    return false
  }

  // Bail if user is not assigned to a variation or we've already tracked this exposure
  if (!decision.variationKey || experimentExposures.has(exposureKey)) {
    return false
  }

  try {
    trackEvent('experiment_viewed', mParticleSdk.EventType.Other, {
      experiment_name: experimentName,
      variant: decision.variationKey,
      app: 'classy-embedded-app',
    })

    experimentExposures.add(exposureKey)

    return true
  } catch (error) {
    logger('warn', new Error('Failed to track experiment exposure'), {
      context: {
        error,
        experimentName,
        flagKey: decision.flagKey,
        variationKey: decision.variationKey,
      },
    })

    return false
  }
}

/**
 * Hook that combines Optimizely feature flag checking with experiment exposure tracking.
 *
 * **IMPORTANT**: This hook is for A/B tests and experiments only.
 * For regular feature flags, use `useFeatureFlag` instead.
 *
 * This hook will:
 * 1. Get the feature flag decision from Optimizely (or URL override)
 * 2. Automatically track an "experiment_viewed" event to mParticle
 * 3. Prevent duplicate tracking for the same experiment variation
 *
 * @param flagName - The Optimizely feature flag name
 * @param experimentName - Human-readable experiment name for tracking
 * (e.g., "NPO Checkout App 2025 AA")
 * @param shouldFireEvent - Whether to fire the event, for conditional hook usage (default: true)
 * @returns The Optimizely decision object with enabled/disabled state and variation info
 *
 * @example
 * ```typescript
 * const { enabled: showNewCheckout } = useTrackedExperiment(
 *   OptimizelyFeatureFlags.npo_checkout_app_2025,
 *   "NPO Checkout App 2025"
 * );
 *
 * if (showNewCheckout) {
 *   // Render treatment variant
 * } else {
 *   // Render control variant
 * }
 * ```
 */
export const useTrackedExperiment = (
  flagName: OptimizelyFeatureFlags,
  experimentName: string,
  shouldFireEvent = true, // allows conditional usage
): OptimizelyDecisionWithoutUserContext => {
  // all experiments are also feature flags :)
  const decision = useFeatureFlag(flagName)
  const { mParticle, consentSynced } = useInternalAnalytics()

  const trackEvent = useTrackEvent()

  useEffect(() => {
    if (mParticle && consentSynced && shouldFireEvent) {
      trackExperimentExposure(decision, experimentName, trackEvent)
    }
  }, [decision, experimentName, trackEvent, mParticle, consentSynced, shouldFireEvent])

  return decision
}

// Testing utilities
export const clearExperimentExposureCache = (): void => {
  experimentExposures.clear()
}

export const getExperimentExposureCache = (): Set<string> => {
  return new Set(experimentExposures)
}
